import { useLayoutEffect, useState } from "react";
import { Modal } from "antd";
import { CalendarOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import type { Reservation } from "../../../api/reservations";
import { useViewport } from "../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../utils/modalStyles";
import { formatClock, toDateKey } from "../logic/dayTime";
import { useNow } from "../hooks/useNow";
import { useNewReservationForm } from "../hooks/useNewReservationForm";
import { useReservationFormat } from "../hooks/useReservationFormat";
import { useEscapeBlurOrClose } from "../hooks/useEscapeBlurOrClose";
import { useSaveReservation } from "../hooks/useSaveReservation";
import { useSuggestTable } from "../hooks/useSuggestTable";
import type { NewReservationSeed } from "../hooks/useReservationsView";
import WhenSection from "../components/newReservation/WhenSection";
import GuestsDurationSection from "../components/newReservation/GuestsDurationSection";
import TablesSection from "../components/newReservation/TablesSection";
import GuestSection from "../components/newReservation/GuestSection";
import MobileTablePicker from "../components/newReservation/MobileTablePicker";

/** Какво отваря модалът: нова (по желание за маса и час) или редакция на предстояща. */
export type ReservationModalTarget =
  | { kind: "new"; seed: NewReservationSeed | null }
  | { kind: "edit"; reservation: Reservation };

/**
 * Съдържанието — монтира се при всяко отваряне (`destroyOnHidden`): същата
 * форма като панела в „Резервации" (`useNewReservationForm`, секциите, записът
 * `useSaveReservation`), само масата се избира с регион и плочки — тук няма
 * схема. Без „Настани" — само създаване и редакция.
 */
const ReservationModalBody = ({
  target,
  regions,
  defaultRegionId,
  enabled,
  onClose,
  onSaved,
}: {
  target: ReservationModalTarget;
  regions: SchemaRegion[];
  defaultRegionId: number | null;
  // false — без интернет (заявките на формата спират).
  enabled: boolean;
  onClose: () => void;
  onSaved: (reservation: Reservation) => void;
}) => {
  const { t } = useTranslation();
  const { isNarrow } = useViewport();
  const format = useReservationFormat();
  const nowMs = useNow();
  const todayKey = toDateKey(new Date(nowMs));
  const form = useNewReservationForm({ regions, nowMs, open: true, enabled });
  const { save, isSaving } = useSaveReservation(form, onSaved);
  // Esc в поле само маха фокуса, извън поле затваря — като панела в „Резервации".
  useEscapeBlurOrClose(onClose);
  const suggestTable = useSuggestTable(form);
  const { draft, editing, reset, startEdit } = form;

  // Регионът на масата, от която е отворен (избраната клетка/резервацията), иначе — показаният.
  const firstTableId = target.kind === "edit" ? target.reservation.tableIds[0] : target.seed?.tableId;
  const [regionId, setRegionId] = useState<number | null>(
    () =>
      regions.find((region) => region.elements.some((element) => element.id === firstTableId))?.id ??
      defaultRegionId ??
      regions[0]?.id ??
      null,
  );

  // Попълването е преди първото рисуване — без миг с празна форма.
  useLayoutEffect(() => {
    if (target.kind === "edit") startEdit(target.reservation);
    else reset(target.seed);
  }, [target, reset, startEdit]);

  const subtitle = [
    format.relativeDay(draft.slot.dateKey, todayKey),
    `${formatClock(form.fromMs)}–${formatClock(form.toMs)}`,
    format.guests(draft.guests),
  ].join(" · ");
  const handleSuggest = () => suggestTable(regionId, (table) => setRegionId(table.regionId));

  return (
    <div className="flex flex-col">
      <div
        className={`flex items-start justify-between gap-3 border-b border-shade-2 ${isNarrow ? "px-4 py-3.5" : "px-6 py-5"}`}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint text-[20px] text-brand-deep-ink">
            <CalendarOutlined />
          </span>
          <div className="min-w-0">
            <div className="text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {t(editing ? "reservations.form.editTitle" : "reservations.form.title")}
            </div>
            <div className="mt-1 truncate text-[13px] font-medium leading-[1.4] tabular-nums text-body">{subtitle}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("reservations.form.close")}
          className="flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[14px] text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          <CloseOutlined />
        </button>
      </div>

      <div
        className={`flex max-h-[min(68dvh,720px)] flex-col gap-5 overflow-y-auto ${isNarrow ? "p-4" : "px-6 pb-6 pt-5"}`}
      >
        <WhenSection
          slot={draft.slot}
          todayKey={todayKey}
          nowMs={nowMs}
          slotPast={form.slotPast}
          slotOutsideHours={form.slotOutsideHours}
          originalSlot={form.originalSlot}
          onDayChange={form.setDay}
          onHourChange={form.setHour}
          onMinuteChange={form.setMinute}
        />
        <GuestsDurationSection
          guests={draft.guests}
          durationMinutes={draft.durationMinutes}
          onGuestsChange={form.setGuests}
          onDurationChange={form.setDuration}
        />
        <TablesSection
          selectedTables={form.selectedTables}
          busySelectedTables={form.busySelectedTables}
          guests={draft.guests}
          seats={form.seats}
          hasEnoughSeats={form.hasEnoughSeats}
          dayState={form.dayError ? "error" : form.isLoadingDay ? "loading" : "ready"}
          onRetryDay={form.retryDay}
          onSuggest={handleSuggest}
          onRemove={form.toggleTable}
          picker={
            regionId !== null ? (
              <MobileTablePicker
                form={form}
                regions={regions}
                activeRegionId={regionId}
                onRegionChange={setRegionId}
                onSuggest={handleSuggest}
              />
            ) : null
          }
        />
        <GuestSection
          draft={draft}
          errors={form.guestErrors}
          isNarrow={isNarrow}
          guestHistory={form.guestHistory}
          onFieldChange={form.setField}
          onFieldBlur={form.touchField}
        />
      </div>

      <div
        className={`flex gap-2 border-t border-shade-2 ${isNarrow ? "flex-col-reverse px-4 py-3" : "px-6 py-4"}`}
      >
        <button
          type="button"
          onClick={onClose}
          className="h-11 shrink-0 cursor-pointer rounded-[10px] border border-shade-3 bg-transparent px-4 text-[14px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          {t("reservations.form.cancel")}
        </button>
        <button
          type="button"
          onClick={() => void save()}
          // Не `disabled`: кликът по изключения бутон показва грешките.
          aria-disabled={!form.canSubmit || isSaving}
          className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border-none bg-brand text-[14px] font-bold text-white transition-colors duration-150 ${
            form.canSubmit && !isSaving ? "cursor-pointer hover:bg-brand-deep" : "cursor-not-allowed opacity-45"
          }`}
        >
          {isSaving && <LoadingOutlined />}
          {t(editing ? "reservations.form.save" : "reservations.form.create")}
        </button>
      </div>
    </div>
  );
};

/**
 * Модалът „Нова резервация" / „Редакция на резервация" на „Таблица на
 * резервациите" — шелът е общият (`modalStyles`, клас Form 560/520). Клик на
 * фона затваря (antd); Esc е наш (`useEscapeBlurOrClose`, `keyboard={false}`):
 * в поле маха фокуса, иначе затваря — Esc докато се пише не трие черновата.
 */
const ReservationModal = ({
  target,
  regions,
  defaultRegionId,
  enabled,
  onClose,
  onSaved,
}: {
  // null — затворен.
  target: ReservationModalTarget | null;
  regions: SchemaRegion[];
  defaultRegionId: number | null;
  enabled: boolean;
  onClose: () => void;
  // Записаната резервация — страницата отива на деня/региона ѝ.
  onSaved: (reservation: Reservation) => void;
}) => {
  const { isMobile, isTablet } = useViewport();
  return (
    <Modal
      open={target !== null}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      width={isMobile ? undefined : isTablet ? 520 : 560}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      {target && (
        <ReservationModalBody
          target={target}
          regions={regions}
          defaultRegionId={defaultRegionId}
          enabled={enabled}
          onClose={onClose}
          onSaved={onSaved}
        />
      )}
    </Modal>
  );
};

export default ReservationModal;
